import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { env } from '@/core/config/env';
import type { PagedResult } from '@/shared/types/pagedResult';
import type { CreatedResponse } from '@/shared/types/createdResponse';
import { decodeStructureJson, encodeStructureJson } from './project-templates.adapter';
import type {
  CreateProjectTemplatePayload,
  InstantiateTemplateResult,
  ProjectTemplate,
  ProjectTemplateFilters,
  ProjectTemplateSummary,
  SaveAsTemplatePayload,
  UpdateTemplateMetadataPayload,
  UpdateTemplateStructurePayload,
} from '../types';
import { projectTemplatesMockState, toProjectTemplateSummary } from './project-templates.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

/** No pagination UI exists for the gallery — request a page large enough to cover every template in one call. */
const LIST_PAGE_SIZE = 100;

function matchesFilters(template: ProjectTemplate, filters: ProjectTemplateFilters): boolean {
  if (filters.sector && template.sector !== filters.sector) return false;
  if (filters.scope === 'system' && !template.isSystem) return false;
  if (filters.scope === 'tenant' && template.isSystem) return false;
  return true;
}

/** DTO shape returned by the backend — `structureJson` is decoded before it reaches the rest of the app. */
interface ProjectTemplateDto {
  id: string;
  tenantId: string | null;
  title: string;
  sector: ProjectTemplate['sector'];
  description: string | null;
  isSystem: boolean;
  structureJson: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

function decodeTemplate(dto: ProjectTemplateDto): ProjectTemplate {
  return {
    id: dto.id,
    tenantId: dto.tenantId,
    title: dto.title,
    sector: dto.sector,
    description: dto.description,
    isSystem: dto.isSystem,
    createdById: dto.createdById,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    ...decodeStructureJson(dto.structureJson),
  };
}

export const projectTemplatesService = {
  getList: async (filters: ProjectTemplateFilters, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const items = projectTemplatesMockState
        .filter((tpl) => matchesFilters(tpl, filters))
        .map(toProjectTemplateSummary);
      return Promise.resolve({ data: items });
    }
    const response = await httpClient.get<PagedResult<ProjectTemplateSummary>>(
      `${API_V1}/project-templates`,
      {
        params: { sector: filters.sector, scope: filters.scope, page: 1, pageSize: LIST_PAGE_SIZE },
        signal,
      },
    );
    return { ...response, data: response.data.items ?? [] };
  },

  getById: async (templateId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const template = projectTemplatesMockState.find((t) => t.id === templateId);
      if (!template) return Promise.reject(new Error('ProjectTemplate.NotFound'));
      return Promise.resolve({ data: template });
    }
    const response = await httpClient.get<ProjectTemplateDto>(
      `${API_V1}/project-templates/${templateId}`,
      { signal },
    );
    return { ...response, data: decodeTemplate(response.data) };
  },

  create: async (payload: CreateProjectTemplatePayload) => {
    if (USE_MOCK) {
      const now = new Date().toISOString();
      const newTemplate: ProjectTemplate = {
        id: `tenant-tpl-${Date.now()}`,
        tenantId: 'mock-org-1',
        title: payload.title,
        sector: payload.sector,
        description: payload.description ?? null,
        isSystem: false,
        createdById: 'mock-user-1',
        createdAt: now,
        updatedAt: now,
        boards: payload.boards,
        customFields: payload.customFields,
        tags: payload.tags,
      };
      projectTemplatesMockState.unshift(newTemplate);
      return Promise.resolve({ data: { id: newTemplate.id } satisfies CreatedResponse });
    }
    return httpClient.post<
      { title: string; sector: string; description?: string; structureJson: string },
      CreatedResponse
    >(`${API_V1}/project-templates`, {
      title: payload.title,
      sector: payload.sector,
      description: payload.description,
      structureJson: encodeStructureJson(payload),
    });
  },

  updateMetadata: async (templateId: string, payload: UpdateTemplateMetadataPayload) => {
    if (USE_MOCK) {
      const idx = projectTemplatesMockState.findIndex((t) => t.id === templateId);
      if (idx === -1) return Promise.reject(new Error('ProjectTemplate.NotFound'));
      if (projectTemplatesMockState[idx].isSystem) {
        return Promise.reject(new Error('ProjectTemplate.SystemPresetImmutable'));
      }
      projectTemplatesMockState[idx] = {
        ...projectTemplatesMockState[idx],
        title: payload.title,
        sector: payload.sector,
        description: payload.description ?? null,
        updatedAt: new Date().toISOString(),
      };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<UpdateTemplateMetadataPayload, void>(
      `${API_V1}/project-templates/${templateId}`,
      payload,
    );
  },

  updateStructure: async (templateId: string, payload: UpdateTemplateStructurePayload) => {
    if (USE_MOCK) {
      const idx = projectTemplatesMockState.findIndex((t) => t.id === templateId);
      if (idx === -1) return Promise.reject(new Error('ProjectTemplate.NotFound'));
      if (projectTemplatesMockState[idx].isSystem) {
        return Promise.reject(new Error('ProjectTemplate.SystemPresetImmutable'));
      }
      projectTemplatesMockState[idx] = {
        ...projectTemplatesMockState[idx],
        boards: payload.boards,
        customFields: payload.customFields,
        tags: payload.tags,
        updatedAt: new Date().toISOString(),
      };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<{ structureJson: string }, void>(
      `${API_V1}/project-templates/${templateId}/structure`,
      { structureJson: encodeStructureJson(payload) },
    );
  },

  delete: async (templateId: string) => {
    if (USE_MOCK) {
      const idx = projectTemplatesMockState.findIndex((t) => t.id === templateId);
      if (idx === -1) return Promise.reject(new Error('ProjectTemplate.NotFound'));
      if (projectTemplatesMockState[idx].isSystem) {
        return Promise.reject(new Error('ProjectTemplate.SystemPresetImmutable'));
      }
      projectTemplatesMockState.splice(idx, 1);
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/project-templates/${templateId}`);
  },

  /** Creates a project (with one board per `structureJson.boards[]` entry) from a template. */
  instantiate: async (templateId: string) => {
    if (USE_MOCK) {
      const template = projectTemplatesMockState.find((t) => t.id === templateId);
      if (!template) return Promise.reject(new Error('ProjectTemplate.NotFound'));
      return Promise.resolve({
        data: { id: `mock-project-${Date.now()}` } satisfies InstantiateTemplateResult,
      });
    }
    return httpClient.post<undefined, InstantiateTemplateResult>(
      `${API_V1}/project-templates/${templateId}/instantiate`,
      undefined,
    );
  },

  /** "Save as template" from an existing project — captures all its boards/tasks/statuses/tags/fields. */
  saveFromProject: async (projectId: string, payload: SaveAsTemplatePayload) => {
    if (USE_MOCK) {
      const now = new Date().toISOString();
      const newTemplate: ProjectTemplate = {
        id: `tenant-tpl-${Date.now()}`,
        tenantId: 'mock-org-1',
        title: payload.title,
        sector: payload.sector,
        description: payload.description ?? null,
        isSystem: false,
        createdById: 'mock-user-1',
        createdAt: now,
        updatedAt: now,
        boards: [],
        customFields: [],
        tags: [],
      };
      projectTemplatesMockState.unshift(newTemplate);
      return Promise.resolve({ data: { id: newTemplate.id } satisfies CreatedResponse });
    }
    return httpClient.post<SaveAsTemplatePayload, CreatedResponse>(
      `${API_V1}/projects/${projectId}/save-as-template`,
      payload,
    );
  },
};
